import React from 'react';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core/styles/index';
import millify from 'millify';
import FadeIn from 'react-fade-in';
import { useMediaQuery } from 'react-responsive';
import breakpoints from './../helpers/breakpoints';
import usePersistentStore from '../../store/persistentStore';
import useStore from '../../store/store';
import {getComparisonTooltip, normalizeComparisonMode} from '../helpers/comparisonPeriod';

const GREEN = '#57cb80';
const RED = '#FF3636';

const Arrow = React.memo(({ direction, invertTrend = false }) => {
  const isGood =
    (direction === 'up' && !invertTrend) || (direction === 'down' && invertTrend);
  const pathD = direction === 'up' ? 'M7 15l5-5 5 5z' : 'M7 10l5 5 5-5z';
  const viewBox = direction === 'up' ? '0 0 20 18' : '0 -4 20 18';

  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox={viewBox} aria-hidden>
      <g fill="none" fillRule="evenodd">
        <path d="M0 0h24v24H0z" />
        <path fill={isGood ? GREEN : RED} fillRule="nonzero" d={pathD} />
      </g>
    </svg>
  );
});

const useStyles = makeStyles(() => ({
  wrapper: {
    top: 7,
    position: 'absolute',
    display: 'inline-grid',
    borderRadius: 4,
    padding: 2,
    margin: 2,
  },
  wrapperMobile: {
    top: 14,
    right: 70,
    display: 'inline-grid',
    borderRadius: 4,
    padding: 2,
    margin: 2,
  },
}));

function getTrendState(current, previous, showHighPercentage, useMillify) {
  const diffNum = (100 - (current * 100) / previous) * -1;
  const valid =
    Number.isFinite(diffNum) &&
    previous !== 0 &&
    (Math.abs(diffNum) <= 10000 || showHighPercentage);

  if (!valid) return null;

  return {
    direction: diffNum < 0 ? 'down' : 'up',
    pct: useMillify
      ? millify(Math.abs(Math.round(diffNum)))
      : Math.abs(Math.round(diffNum)),
  };
}

const Trend = React.memo((props) => {
  const {
    current,
    previous,
    invertTrend = false,
    showHighPercentage,
    inDropdown,
    big,
    millify: useMillify,
  } = props;

  const classes = useStyles();
  const isMobile = useMediaQuery({ query: breakpoints.mobile });
  const comparisonEnabled = usePersistentStore((state) => state.comparisonEnabled);
  const comparisonMode = usePersistentStore((state) => state.comparisonMode);
  const period = useStore((state) => state.period);
  const comparisonPeriod = useStore((state) => state.comparisonPeriod);

  const state = getTrendState(current, previous, showHighPercentage, useMillify);
  if (!state || !comparisonEnabled) return null;

  const { direction, pct } = state;
  const arrow = <Arrow direction={direction} invertTrend={invertTrend} />;
  const tooltipTitle =
    props.tooltip != null
      ? props.tooltip
      : getComparisonTooltip(period, comparisonPeriod, normalizeComparisonMode(comparisonMode));

  const labelWithTooltip = (tooltipProps) => (
    <>
      <Tooltip title={tooltipTitle} {...tooltipProps}>
        <span>{arrow}</span>
      </Tooltip>
      {pct}%
    </>
  );
  const labelWithoutTooltip = (
    <>
      {arrow}
      {pct}%
    </>
  );

  if (isMobile) {
    return (
      <div className={classes.wrapperMobile}>
        <Typography variant="caption">
          {labelWithTooltip({ arrow: true, enterTouchDelay: 60 })}
        </Typography>
      </div>
    );
  }

  if (inDropdown) {
    return (
      <Typography variant={big ? 'h6' : 'caption'} display="inline">
        {labelWithoutTooltip}
      </Typography>
    );
  }

  return (
    <div className={classes.wrapper}>
      <FadeIn>
        <Typography variant="caption">
          {labelWithTooltip({ arrow: true })}
        </Typography>
      </FadeIn>
    </div>
  );
});

export default Trend;
